import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartnerOverview } from '../../api.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import { describeError } from '../../errors.js';
import { DataTable } from '../../components/DataTable.js';
import { toCredits } from '../../components/AllocationPanel.js';

type TenantCredit = PartnerOverview['tenantCredits'][number];

/** Granted/remaining render as `Unlimited` for unlimited pools and as whole
 *  credits otherwise (missing amounts show 0 — see plan product decision). */
function creditCell(credit: TenantCredit['credit'], amount: number | undefined, unlimitedLabel: string) {
  return credit.unlimited ? unlimitedLabel : toCredits(amount ?? 0);
}

export function PartnerOverviewTab({ http, onToast, active }: { http: AdminHttpClient; onToast: (toast: ToastMessage) => void; active: boolean }) {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<PartnerOverview>();
  useEffect(() => {
    if (!active) return;
    void http.partnerOverview().then(setOverview).catch((error) => onToast({ tone: 'error', message: describeError(error, t) }));
  }, [active, http, onToast, t]);
  if (!overview) return <section className="panel"><h2>{t('partner.tabOverview')}</h2></section>;
  const credit = overview.credit;
  const unlimitedLabel = t('partner.creditUnlimitedShort');
  const columns = [
    { key: 'tenant', header: t('partner.colTenant'), render: (row: TenantCredit) => <><strong>{row.name}</strong><span className="muted"> {row.tenantId}</span></> },
    { key: 'granted', header: t('partner.creditGranted'), render: (row: TenantCredit) => creditCell(row.credit, row.credit.granted, unlimitedLabel) },
    { key: 'consumed', header: t('partner.creditConsumed'), render: (row: TenantCredit) => toCredits(row.credit.consumed) },
    { key: 'remaining', header: t('partner.creditRemaining'), render: (row: TenantCredit) => creditCell(row.credit, row.credit.remaining, unlimitedLabel) },
  ];
  return <section className="content-stack">
    <section className="panel">
      <h2>{overview.name}</h2>
      <p className="muted">{t('partner.overviewTenants', { count: overview.tenantCount })}</p>
      <div className="metric-grid">
        <div><strong>{creditCell(credit, credit.granted, unlimitedLabel)}</strong><span>{t('partner.creditGranted')}</span></div>
        <div><strong>{toCredits(credit.consumed)}</strong><span>{t('partner.creditConsumed')}</span></div>
        <div><strong>{creditCell(credit, credit.remaining, unlimitedLabel)}</strong><span>{t('partner.creditRemaining')}</span></div>
      </div>
    </section>
    <section className="panel">
      <h2>{t('partner.tenantCreditsTitle')}</h2>
      <DataTable rows={overview.tenantCredits} columns={columns} empty={t('partner.none')} getRowKey={(row) => row.tenantId} mobileCards />
    </section>
    <section className="panel"><h2>{t('partner.overviewUsage')}</h2><div className="metric-grid"><div><strong>{overview.usage.requests}</strong><span>{t('partner.overviewRequests')}</span></div><div><strong>{overview.usage.webSearchRequests}</strong><span>{t('partner.overviewWebSearch')}</span></div><div><strong>{overview.usage.imageInputCount}</strong><span>{t('partner.overviewImageInputs')}</span></div></div><p className="muted">{overview.usage.from} – {overview.usage.to}</p></section>
    <section className="panel"><h2>{t('partner.modelScope')}</h2>{overview.modelScope.length ? <ul className="plain-list">{overview.modelScope.map((model) => <li key={model}>{model}</li>)}</ul> : <p className="muted">{t('partner.noModelsInScope')}</p>}</section>
  </section>;
}
